/* ── Shared vehicle ↔ product tag matching ──────────────────────────────────
   Single source of truth for the slugify/candidate-tag/match algorithm that
   vehicle-fitment-collection.js and section-category-spotlight.js (and any
   future section) need to flag products against the active garage vehicle.
   Exposed on window.LakiFitment since plain <script> tags here have no
   module bundler to share code through otherwise. Must load before any file
   that calls into it — see layout/theme.liquid script order.
   ───────────────────────────────────────────────────────────────────────── */
(() => {
  const GARAGE_KEY = 'circuit:build-garage';
  const ACTIVE_KEY = 'circuit:build-active-id';

  const getAllVehicles = () => {
    try {
      return JSON.parse(localStorage.getItem(GARAGE_KEY) || '[]');
    } catch (_) {
      return [];
    }
  };

  const getActiveVehicle = () => {
    try {
      const activeId = localStorage.getItem(ACTIVE_KEY) || '';
      return getAllVehicles().find((v) => v.id === activeId) || null;
    } catch (_) {
      return null;
    }
  };

  // Drop any part (e.g. brand) already repeated inside a later, more
  // specific part, so "Acer / Chromebook / Acer 11 C721" doesn't render as
  // "Acer Chromebook Acer 11 C721".
  const buildLabel = (vehicle) => {
    const parts = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean);
    return parts
      .filter((part, i) => {
        const partLower = part.toLowerCase();
        return !parts.slice(i + 1).some((later) => later.toLowerCase().includes(partLower));
      })
      .join(' ');
  };

  const slugify = (value) =>
    (value || '')
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

  // Merchants tag products with whichever precision they have on hand: a
  // full year+make+model tag for an exact fit, a make+model tag to cover
  // every year of that model, or just a make tag for a universal fit.
  // Two conventions are supported so merchants don't need a specific format:
  //   fits_ prefix style:  fits_2019_toyota_highlander
  //   Raw Shopify tag style: 2019 toyota highlander
  // Matching any one candidate (after normalization) is enough to flag the product.
  // Candidates are split into two buckets because they carry very different
  // matching risk:
  //   fuzzy  — identify a specific model (year+make+model, make+model, or
  //            model alone). Safe to substring-match, since a tag like
  //            "Latitude 14 7420 Laptop" or "C721" is still unambiguously
  //            that one device.
  //   exact  — identify ONLY the make/series ("chromebook", "fits_toyota").
  //            A merchant CAN tag a product with just the make for a
  //            genuine universal fit, but "make" here is often a broad
  //            series name (e.g. "Chromebook") shared across many brands'
  //            products — substring-matching it would flag an HP Chromebook
  //            cable as fitting an Acer Chromebook just because both tags
  //            happen to contain the word "chromebook". So these only match
  //            a tag that equals them exactly, never a containment match.
  const buildCandidateTags = (vehicle) => {
    if (!vehicle || !vehicle.make) return { fuzzy: [], exact: [] };
    const make = slugify(vehicle.make);
    const model = slugify(vehicle.model);
    const year = slugify(vehicle.year);
    const rawMake = (vehicle.make || '').toLowerCase().trim();
    const rawModel = (vehicle.model || '').toLowerCase().trim();
    const rawYear = String(vehicle.year || '').trim();
    const fuzzy = [];
    const exact = [];
    // fits_ prefix convention
    if (year && model) fuzzy.push(`fits_${year}_${make}_${model}`);
    if (model) fuzzy.push(`fits_${make}_${model}`);
    exact.push(`fits_${make}`);
    if (model) fuzzy.push(`fits_${model}`);
    // Raw "year make model" Shopify tag convention
    if (rawYear && rawModel) fuzzy.push(`${rawYear} ${rawMake} ${rawModel}`);
    if (rawModel) fuzzy.push(`${rawMake} ${rawModel}`);
    exact.push(rawMake);
    // Bare model number alone — the convention this catalog's products
    // actually use (e.g. a product just tagged "C721", with no brand/series
    // prefix at all).
    if (rawModel) fuzzy.push(rawModel);
    return { fuzzy, exact };
  };

  // Strip every non-alphanumeric character so "f-150", "f_150", and "f150"
  // all compare equal. Merchants type tags by hand and won't always mirror
  // the underscore slugify() inserts for hyphened model names like "F-150".
  const normalize = (s) =>
    (s || '')
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  // Real-world product tags don't come back as a clean exact match for the
  // candidates above: a product tagged "Latitude 14 7420 Laptop" or
  // "IdeaPad Yoga 13 (20175)" has brand dropped, extra suffix words added,
  // or an internal part number appended, so a tag will never equal a
  // candidate character-for-character. Instead check whether one normalized
  // string contains the other. MIN_MATCH_LENGTH guards against trivial
  // short strings (e.g. a 2-char candidate) matching almost anything.
  const MIN_MATCH_LENGTH = 3;

  const segmentMatchesCandidate = (segment, candidate) => {
    if (!segment || !candidate) return false;
    if (segment === candidate) return true;
    if (segment.length < MIN_MATCH_LENGTH || candidate.length < MIN_MATCH_LENGTH) return false;
    return segment.includes(candidate) || candidate.includes(segment);
  };

  const hasMatch = (value, candidates) => {
    if (!value) return false;
    const normFuzzy = candidates.fuzzy.map(normalize).filter(Boolean);
    const normExact = candidates.exact.map(normalize).filter(Boolean);
    const values = value.split(',').map((v) => normalize(v.trim())).filter(Boolean);
    return values.some(
      (v) => normExact.includes(v) || normFuzzy.some((c) => segmentMatchesCandidate(v, c))
    );
  };

  const matchesCard = (card, candidates) =>
    (candidates.fuzzy.length > 0 || candidates.exact.length > 0) &&
    (hasMatch(card.dataset.productTags, candidates) || hasMatch(card.dataset.productFitment, candidates));

  // Finds every [data-vehicle-fitment-chip] within `root` and toggles its
  // `hidden` attribute based on whether its nearest data-product-tags /
  // data-product-fitment ancestor matches the active vehicle. Callers that
  // need the match result for something else (sorting, hard-hiding) should
  // call matchesCard() themselves instead — this is only for the simple case.
  const applyChips = (root) => {
    const scope = root || document;
    const vehicle = getActiveVehicle();
    const candidates = buildCandidateTags(vehicle);
    scope.querySelectorAll('[data-product-tags], [data-product-fitment]').forEach((card) => {
      const chip = card.querySelector('[data-vehicle-fitment-chip]');
      if (!chip) return;
      chip.hidden = !matchesCard(card, candidates);
    });
  };

  window.LakiFitment = {
    getAllVehicles,
    getActiveVehicle,
    buildLabel,
    slugify,
    buildCandidateTags,
    hasMatch,
    matchesCard,
    applyChips
  };
})();
